import { useCallback } from 'react'
import type { ActivityForm } from '../typings'
import { FormikErrors } from 'formik'
import { getHours, getMinutes, isAfter, isEqual, set } from 'date-fns/esm'

function constructDate(date: Date | number | null, time: Date | number | null) {
  if (!date) return null

  if (time && !isNaN(Number(time))) {
    return set(date, {
      hours: getHours(time),
      minutes: getMinutes(time),
    })
  }

  const d = new Date(date)
  d.setHours(0, 0, 0)
  return d
}

export function useValidations(attachToMode?: boolean, ownerError?: string) {
  const validate = useCallback(
    (model: ActivityForm) => {
      const {
        subject,
        startDate,
        dueDate,
        startTime,
        dueTime,
        activityTypeId,
        ownerId,
      } = model

      const errors: FormikErrors<ActivityForm> = {}

      const constructedStartDate = constructDate(startDate, startTime)
      const constructedDueDate = constructDate(dueDate, dueTime)

      if (!subject) {
        errors.subject = 'Required'
      }

      if (!activityTypeId) {
        errors.activityTypeId = 'Required'
      }

      if (!ownerId) {
        errors.ownerId = 'Required'
      } else if (ownerError) {
        errors.ownerId = ownerError
      }

      // SH-2338: in calendar "Create Event" mode the Attach-To selection is
      // optional — an activity with no lead or opportunity is a personal event.
      // Both fields are still written together by the picker, so flag only the
      // inconsistent half-selected state, which a user cannot reach through the
      // UI but a malformed model could.
      if (attachToMode) {
        const { entityId, entityType } = model as ActivityForm & {
          entityId?: number | null
          entityType?: number | null
        }
        if ((entityId == null) !== (entityType == null)) {
          ;(
            errors as FormikErrors<ActivityForm> & { entityId?: string }
          ).entityId = 'Select a lead or opportunity, or leave empty'
        }
      }

      if (!dueDate) {
        errors.dueDate = 'Required'
      } else if (
        startDate &&
        dueDate &&
        (isAfter(constructedStartDate || 0, constructedDueDate || 0) ||
          isEqual(constructedDueDate || 0, constructedStartDate || 0))
      ) {
        errors.dueDate = 'Must be after start date'
      } else if (dueDate.toString() === 'Invalid Date') {
        errors.dueDate = 'Invalid date format'
      }

      if (startTime && startTime.toString() === 'Invalid Date') {
        errors.startTime = 'Invalid date format'
      }

      if (dueTime && dueTime.toString() === 'Invalid Date') {
        errors.dueTime = 'Invalid date format'
      }

      if (!dueTime) {
        errors.dueTime = 'Required'
      }

      if (startDate && startDate.toString() === 'Invalid Date') {
        errors.startDate = 'Invalid date format'
      }

      if (startDate && !startTime) {
        errors.startTime = 'Required'
      }

      return errors
    },
    [constructDate, isAfter, isEqual, attachToMode, ownerError],
  )

  return { validate }
}
